import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Send, ArrowLeft, Gamepad2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ContactForm {
  email: string;
  subject: string;
  message: string;
  honeypot: string;
}

export default function Contact() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<ContactForm>({
    email: "",
    subject: "",
    message: "",
    honeypot: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");

  // Get user type and email
  useEffect(() => {
    if (user) {
      const fetchUserData = async () => {
        try {
          // Get user type from profiles
          const { data: profileData } = await supabase
            .from("profiles")
            .select("user_type")
            .eq("id", user.id)
            .single();

          if (profileData?.user_type) {
            setUserType(profileData.user_type);
          }

          // Get email from auth.users (Supabase Auth)
          if (user.email) {
            setUserEmail(user.email);
            setForm(prev => ({ ...prev, email: user.email }));
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      };

      fetchUserData();
    }
  }, [user?.id]);

  // Handle Turnstile success callback
  useEffect(() => {
    (window as any).onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
    };
    return () => {
      delete (window as any).onTurnstileSuccess;
    };
  }, []);

  // Get subjects based on user type
  const getSubjects = () => {
    const baseSubjects = [
      "General Inquiry",
      "Technical Support",
      "Account Issues",
      "Bug Report"
    ];

    if (!userType) return baseSubjects;

    switch (userType) {
      case "player":
        return [
          ...baseSubjects,
          "Coins & Missions",
          "Game Purchase Issues",
          "Player Profile Problems",
          "Badges & Achievements"
        ];

      case "creator":
        return [
          ...baseSubjects,
          "Key Request Issues",
          "Channel Verification"
        ];

      case "developer":
        return [
          ...baseSubjects
        ];

      default:
        return baseSubjects;
    }
  };

  const handleInputChange = (field: keyof ContactForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot - if filled, it's likely spam
    if (form.honeypot) {
      toast.error("Spam detected. Please try again.");
      return;
    }

    // Validate required fields
    if (!form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get Supabase URL and anon key from environment variables
      // Try both possible variable names for the key
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      if (!supabaseUrl) {
        console.error('VITE_SUPABASE_URL is missing');
        throw new Error("Configuration Supabase manquante : VITE_SUPABASE_URL n'est pas défini. Vérifiez votre fichier .env");
      }

      if (!supabaseAnonKey) {
        console.error('VITE_SUPABASE_ANON_KEY and VITE_SUPABASE_PUBLISHABLE_KEY are both missing');
        throw new Error("Configuration Supabase manquante : VITE_SUPABASE_ANON_KEY ou VITE_SUPABASE_PUBLISHABLE_KEY doit être défini. Vérifiez votre fichier .env");
      }

      // Get auth token if user is logged in
      const { data: { session } } = await supabase.auth.getSession();
      const authHeader = session ? `Bearer ${session.access_token}` : undefined;

      // Call the Edge function to send the email
      let response: Response;
      try {
        response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader || `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            email: form.email,
            subject: form.subject,
            message: form.message,
            userType: userType || null,
            turnstileToken: (window as any).turnstile?.getResponse() || turnstileToken,
          }),
        });
      } catch (fetchError: any) {
        console.error('Fetch error:', fetchError);
        // Check if function is deployed
        if (fetchError.message?.includes('Failed to fetch') || fetchError.name === 'TypeError') {
          throw new Error("La fonction d'envoi d'email n'est pas accessible. Veuillez déployer la fonction Edge 'send-contact-email' ou contacter le support technique.");
        }
        throw fetchError;
      }

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        const text = await response.text();
        console.error('Response text:', text);
        throw new Error(`Erreur de réponse du serveur: ${response.status} ${response.statusText}`);
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      toast.success("Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.");

      // Reset form
      setForm({
        email: user?.email || "",
        subject: "",
        message: "",
        honeypot: ""
      });

    } catch (error: any) {
      console.error("Error sending contact form:", error);
      toast.error(error.message || "Échec de l'envoi du message. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Support"
        description="Get in touch with the IndieKeys team for support, questions, or feedback."
      />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Contact Support</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Get in Touch</h2>
              <p className="text-muted-foreground text-[0.8rem]">
                Have a question or need help? Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <Label htmlFor="email">Email *</Label>
                {user ? (
                  <div className="mt-1 p-0 text-foreground border border-transparent rounded-md">
                    {form.email}
                  </div>
                ) : (
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                )}
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Select value={form.subject} onValueChange={(value) => handleInputChange("subject", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {getSubjects().map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  placeholder="Please describe your issue or question in detail..."
                  rows={6}
                  required
                />
              </div>

              {/* Turnstile Widget */}
              <div className="flex justify-center mb-6">
                <div
                  className="cf-turnstile"
                  data-sitekey="0x4AAAAAAACbx5Lh61AM6GeQ3"
                  data-callback="onTurnstileSuccess"
                ></div>
              </div>

              {/* Honeypot field - hidden from users */}
              <div style={{ display: 'none' }}>
                <Label htmlFor="honeypot">Leave this field empty</Label>
                <Input
                  id="honeypot"
                  type="text"
                  value={form.honeypot}
                  onChange={(e) => handleInputChange("honeypot", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Gamepad2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>

              <div className="mt-4 text-[0.7rem] text-muted-foreground leading-relaxed">
                By submitting this form, you agree that your data will be processed to handle your request. In accordance with GDPR, you have the right to access, rectify, and delete your data. For more information, please see our <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
              </div>
            </form>

            {/* Additional Info */}
            <div className="mt-8 pt-4 border-t flex items-center gap-2">
              <h3 className="font-semibold text-xs">Response Time</h3>
              <p className="text-muted-foreground text-xs">
                Within 48-72 hours during business days.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </Layout >
  );
}
